import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  copyLanguageChannelPairs,
  LanguageChannelPair,
  LanguageSelectorConfig,
} from '@/features/projects/projectsSlice';
import {
  ChannelListManager,
  getChannelListByEpgId,
  MAX_LANGUAGE_PAIRS,
} from '@/features/channels/channelsSlice';
import { useAppSelector } from '@/app/store';
import { LanguageInfo } from '@/features/languages/languagesSlice';

import CheckBox from '@/components/Common/CheckBox';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';
import SectionRule from '@/components/Common/SectionRule';

import { getNewLanguageChannelPair, validateLanguageSelector } from './languageSelectorValidation';

// Re-exported so existing imports (Project.tsx's save gate, tests) can keep
// reading `from './LanguageSelectorSettings'` — the logic itself lives in
// languageSelectorValidation.ts, dependency-free on purpose (see that file).
export {
  getNewLanguageChannelPair,
  validateLanguageSelector,
  type LanguageSelectorRowIssues,
  type LanguageSelectorValidation,
} from './languageSelectorValidation';

const Hint = (props: {children?: React.ReactNode}) => (
  <span className="text-[--Dark-Grey] text-[12px] leading-[16px]">
    { props.children }
  </span>
);

const Warning = (props: {children?: React.ReactNode}) => (
  <FlexRow align="start"
           className="gap-[6px]">
    <span className="material-icons !text-[--Primetime-main] text-[16px] leading-[16px]">
      warning
    </span>
    <span className="text-[--Primetime-main] text-[12px] leading-[16px]">
      { props.children }
    </span>
  </FlexRow>
);

// A borderless icon affordance. HeroUI's Button always draws a box, and three
// boxed buttons on every choice fight the choice itself for attention.
const IconButton = (props: {
  icon:					string;
  title:				string;
  danger?:				boolean;
  onClick:				() => void;
}) => (
  <span className={[
          'material-icons cursor-pointer text-[20px] leading-[20px] select-none !text-[--Dark-Grey]',
          props.danger
          ?	'hover:!text-[--Warning]'
          :	'hover:!text-[--Primetime-main]',
        ].join(' ')}
        role="button"
        title={props.title}
        onClick={props.onClick}>
    { props.icon }
  </span>
);

export interface LanguageSelectorSettingsProps {
  config:				LanguageSelectorConfig;
  setConfig:			(cfg: LanguageSelectorConfig) => void;
  // Combined visible + hidden lists (global and hotel-local). Hidden lists
  // are NOT filtered out — a hidden, curated per-language lineup is the main
  // use case for this feature.
  lists:				ChannelListManager;
  isLoaded?:			boolean;
  isReadOnly?:			boolean;
  triedSave?:			boolean;
  // The card header (globe, title, on-air pill). ProjectRoom.tsx heads the
  // whole inherit/override section with its own CatHeader, so it turns this
  // off rather than titling the same thing twice.
  showTitle?:			boolean;
}

// LanguageSelectorSettings edits the (name, TV language, channel list)
// choices — at project level (bound to HotelLimits.language_selector) or at
// room level (bound to the room's own override). The caller decides which
// config this is: this component is a plain controlled editor over a
// LanguageSelectorConfig and does not know or care which scope it is in.
//
// Order matters on the wire: `order` is renumbered 1..n on every structural
// edit (add/remove/move) because the backend differ matches slice elements
// BY VALUE — a positional-only reorder produces an empty changelog and
// silently fails to save (§3.1).
//
// The choices are a LIST, so they are shown as one: each row is a summary you
// read at a glance — language badge, name, which lineup it switches to — and
// the three fields open on demand. Rendering every row's full form at once
// turned four choices into a wall of twelve inputs, in which the ordering, the
// thing that decides what the guest sees first, was impossible to make out.
const LanguageSelectorSettings = (props: LanguageSelectorSettingsProps) => {
  const { t } = useTranslation();

  const loaded = props.isLoaded === undefined ? true : props.isLoaded;
  const readOnly = props.isReadOnly === true;

  const config: LanguageSelectorConfig = React.useMemo(
    () => props.config ? props.config : { enabled: false, pairs: [] },
    [props.config]
  );

  const pairs = config.pairs || [];

  const validation = React.useMemo(() => validateLanguageSelector(config), [config]);

  // One row open at a time — the pencil is a "work on this one" gesture, and a
  // single index sidesteps the whole class of bugs where a set of open indices
  // quietly points at the wrong rows after a reorder.
  const [editing, setEditing] = React.useState(null as number | null);

  const [dragFrom, setDragFrom] = React.useState(null as number | null);
  const [dragOver, setDragOver] = React.useState(null as number | null);

  // The configured TV languages (/languages.json, loaded into the store at
  // startup) — the same catalog Channel.tsx offers, not a hardcoded list.
  const languages = useAppSelector((state) => state.languages);

  const localeOpts: FieldInfo[] = (languages.languages || []).map((lang: LanguageInfo) => ({
    value:		lang.code,
    desc:		lang.name,
  }));

  const listOpts: FieldInfo[] = (props.lists?.lists || []).map((list) => ({
    value:		list.epg_id,
    desc:		list.name + (list.hidden ? t('projects:project:language_selector:hidden_suffix') : ''),
  }));

  const renumber = (rows: LanguageChannelPair[]): LanguageChannelPair[] =>
    rows.map((pair: LanguageChannelPair, i: number) => ({ ...pair, order: i + 1 }));

  const commit = (rows: LanguageChannelPair[]) => {
    props.setConfig({
      enabled:	config.enabled,
      pairs:		renumber(rows),
    });
  };

  const updateEnabled = (val: boolean) => {
    if (readOnly)
      return;

    props.setConfig({ ...config, enabled: val });
  };

  const updatePair = (i: number, patch: Partial<LanguageChannelPair>) => {
    if (readOnly)
      return;

    const rows = copyLanguageChannelPairs(pairs);
    rows[i] = { ...rows[i], ...patch };
    commit(rows);
  };

  const addPair = () => {
    if (	readOnly ||
			pairs.length >= MAX_LANGUAGE_PAIRS
	   )
      return;

    const rows = copyLanguageChannelPairs(pairs);
    rows.push(getNewLanguageChannelPair(rows.length + 1));
    commit(rows);
    // A blank row has nothing to summarise, so it opens straight into its form.
    setEditing(rows.length - 1);
  };

  // Indices shift under `editing` on every structural edit. Left alone it would
  // keep pointing at a POSITION rather than a row, and the open form would
  // appear to jump to whichever choice slid into the gap.
  const removePair = (i: number) => {
    if (readOnly)
      return;

    const rows = copyLanguageChannelPairs(pairs);
    rows.splice(i, 1);
    commit(rows);

    setEditing(	editing === null || editing === i
				?	null
				:	(editing > i ? editing - 1 : editing)
			  );
  };

  const movePair = (from: number, to: number) => {
    if (	readOnly ||
			from === to ||
			from < 0 || from >= pairs.length ||
			to   < 0 || to   >= pairs.length
	   )
      return;

    const rows = copyLanguageChannelPairs(pairs);
    const [row] = rows.splice(from, 1);
    rows.splice(to, 0, row);
    commit(rows);

    if (editing === from) {
      setEditing(to);
      return;
    }

    if (editing === null)
      return;

    // The open row did not move itself, but closing the hole and re-inserting
    // can still shift it by one in either direction.
    let next = editing;

    if (from < editing)	next -= 1;
    if (to  <= next)	next += 1;

    setEditing(next);
  };

  const triedSave = props.triedSave === true;
  const showTitle = props.showTitle === undefined ? true : props.showTitle;

  const rowHasIssue = (i: number) => {
    const issues = validation.rows[i] || {};

    return !!(issues.label || issues.lang || issues.list);
  };

  // A row with an error opens itself once Save has been attempted. Left
  // collapsed it would put the message on a field nobody can see, and the page
  // would refuse to save with no visible reason why.
  const isOpen = (i: number) =>
    editing === i ||
    (triedSave && rowHasIssue(i));

  return (
  <FlexRow direction="column"
           align="start"
           className="w-full max-w-[720px] rounded-[8px] border-solid border-1 border-[--Lines] bg-white overflow-hidden">
  {showTitle &&
    <FlexRow align="center"
             className="gap-[12px] px-[24px] py-[16px] border-solid border-0 border-b-1 border-[--Lines]">
      <FlexRow justify="center"
               align="center"
               width="w-[44px]"
               className="shrink-0 h-[44px] rounded-[8px] bg-[--primetime-30]">
        <span className="material-icons !text-[--Primetime-main] text-[22px]">
          language
        </span>
      </FlexRow>
      <FlexRow direction="column"
               align="start"
               className="gap-[2px] min-w-0">
        <span className="b14 !text-black">
          { t('projects:project:language_selector:card_title') }
        </span>
        <Hint>
          { t('projects:project:language_selector:card_subtitle') }
        </Hint>
      </FlexRow>
    {/* Whether the guest ever sees any of this is the one thing worth knowing
        before reading the rows, and the toggle that decides it sits below the
        fold once the list gets long. */}
      <FlexRow justify="end"
               flex={0}
               className="shrink-0">
        <FlexRow align="center"
                 flex={0}
                 className={[
                   'gap-[6px] shrink-0 rounded-full px-[10px] py-[4px] whitespace-nowrap',
                   config.enabled ? 'bg-[#DCFCE7]' : 'bg-[#FEF3C7]',
                 ].join(' ')}>
          <span className={[
                  'w-[6px] h-[6px] rounded-full shrink-0',
                  config.enabled ? 'bg-[#16A34A]' : 'bg-[#D97706]',
                ].join(' ')} />
          <span className={[
                  'text-[12px] font-medium leading-[16px]',
                  config.enabled ? '!text-[#166534]' : '!text-[#92400E]',
                ].join(' ')}>
            { config.enabled
              ?	t('projects:project:language_selector:status_shown')
              :	t('projects:project:language_selector:status_hidden')
            }
          </span>
        </FlexRow>
      </FlexRow>
    </FlexRow>
  }

    <FlexRow direction="column"
             align="start"
             className="gap-[16px] px-[24px] py-[20px]">
      <FlexRow align="start"
               className="gap-[16px]">
        <FlexRow flex={0}
                 className="shrink-0 pt-[2px]">
          <CheckBox toggled={config.enabled}
                    isLoaded={loaded}
                    setToggled={readOnly ? () => {} : updateEnabled} />
        </FlexRow>
        <FlexRow direction="column"
                 align="start"
                 className="gap-[4px]">
          <span className="b14 !text-black">
            { t('projects:project:language_selector:enabled') }
          </span>
          <Hint>
            { t('projects:project:language_selector:enabled_hint') }
          </Hint>
        </FlexRow>
      </FlexRow>

      <SectionRule caption={t('projects:project:language_selector:choices')} />

    {pairs.length == 0 &&
      <Hint>
        { t('projects:project:language_selector:empty') }
      </Hint>
    }

    {pairs.map((pair: LanguageChannelPair, i: number) => {
      const issues = validation.rows[i] || {};
      const list = pair.list_id ? getChannelListByEpgId(pair.list_id, props.lists) : null;
      const listFound = !pair.list_id || !!list;
      const open = isOpen(i);

      return (
      <div key={i}
           draggable={!readOnly && !open}
           className={[
                 'flex flex-col items-start w-full rounded-[8px] border-solid border-1 bg-white',
                 dragOver === i && dragFrom !== null && dragFrom !== i
                 ?	'border-[--Primetime-main]'
                 :	'border-[--Lines]',
                 dragFrom === i ? 'opacity-50' : '',
               ].join(' ')}
           onDragStart={(evt: React.DragEvent) => {
                 setDragFrom(i);
                 evt.dataTransfer.effectAllowed = 'move';
                 // Firefox starts no drag at all unless the event carries data.
                 evt.dataTransfer.setData('text/plain', String(i));
               }}
           onDragOver={(evt: React.DragEvent) => {
                 if (dragFrom === null)
                   return;

                 evt.preventDefault();
                 evt.dataTransfer.dropEffect = 'move';

                 if (dragOver !== i)
                   setDragOver(i);
               }}
           onDrop={(evt: React.DragEvent) => {
                 evt.preventDefault();

                 if (dragFrom !== null)
                   movePair(dragFrom, i);

                 setDragFrom(null);
                 setDragOver(null);
               }}
           onDragEnd={() => {
             setDragFrom(null);
             setDragOver(null);
           }}>
        <FlexRow align="center"
                 className="gap-[12px] px-[16px] py-[12px]">
          <span className={[
                  'material-icons text-[20px] !text-[--Mid-Grey] shrink-0 select-none',
                  readOnly || open ? 'cursor-default' : 'cursor-grab',
                ].join(' ')}
                title={t('projects:project:language_selector:reorder')}>
            drag_indicator
          </span>

          <FlexRow justify="center"
                   align="center"
                   width="w-[40px]"
                   className="shrink-0 h-[40px] rounded-full bg-[--primetime-30]">
            <span className="text-[13px] font-semibold !text-[--Primetime-main] uppercase">
              { pair.lang ? pair.lang : '—' }
            </span>
          </FlexRow>

          <FlexRow direction="column"
                   align="start"
                   className="gap-[2px] min-w-0">
            <span className={[
                    'b14 truncate max-w-full',
                    pair.label ? '!text-black' : '!text-[--Dark-Grey]',
                  ].join(' ')}>
              { pair.label
                ?	pair.label
                :	t('projects:project:language_selector:untitled')
              }
            </span>
            <FlexRow align="center"
                     className="gap-[6px] flex-wrap">
              <Hint>
                { t('projects:project:language_selector:list') }
              </Hint>
            {pair.list_id
              ?	<span className={[
                    'rounded-[4px] px-[8px] py-[2px] text-[12px] font-medium leading-[16px] max-w-full truncate',
                    listFound
                    ?	'bg-[--Light-Grey] !text-[--Primetime-main]'
                    :	'bg-[#FEF3C7] !text-[#92400E]',
                  ].join(' ')}>
                  { list ? list.name : pair.list_id }
                </span>
              :	<Hint>
                  { t('projects:project:language_selector:list_placeholder') }
                </Hint>
            }
            </FlexRow>
          </FlexRow>

        {!readOnly &&
          <FlexRow justify="end"
                   flex={0}
                   className="gap-[12px] shrink-0">
          {/* Keyed off `editing`, not `open`. A row forced open by a failed
              save cannot be collapsed — offering a collapse arrow there would
              promise something the click does not do. */}
            <IconButton icon={editing === i ? 'expand_less' : 'edit'}
                        title={editing === i
                               ?	t('projects:project:language_selector:close')
                               :	t('projects:project:language_selector:edit')
                              }
                        onClick={() => setEditing(editing === i ? null : i)} />
            <IconButton icon="delete"
                        danger
                        title={t('projects:project:language_selector:remove')}
                        onClick={() => removePair(i)} />
          </FlexRow>
        }
        </FlexRow>

      {open &&
        <FlexRow direction="column"
                 align="start"
                 className="gap-[12px] px-[16px] pb-[16px] pt-[4px]">
          <FlexRow className="w-full gap-[12px] flex-wrap"
                   align="start">
            <Input label={t('projects:project:language_selector:label')}
                   placeholder={t('projects:project:language_selector:label_placeholder')}
                   isLoaded={loaded}
                   isReadOnly={readOnly}
                   isInvalid={triedSave && !!issues.label}
                   errorMessage={issues.label ? t('projects:project:language_selector:' + issues.label) : undefined}
                   value={pair.label}
                   onValueChange={(val: string) => updatePair(i, { label: val })} />
            <Dropdown label={t('projects:project:language_selector:lang')}
                      isLoaded={loaded}
                      isDisabled={readOnly}
                      isInvalid={triedSave && !!issues.lang}
                      errorMessage={issues.lang ? t('projects:project:language_selector:' + issues.lang) : undefined}
                      value={pair.lang}
                      options={localeOpts}
                      setValue={(val) => updatePair(i, { lang: val ? val.toString() : '' })}>
              <>
              </>
            </Dropdown>
            <Dropdown label={t('projects:project:language_selector:list')}
                      isLoaded={loaded}
                      isDisabled={readOnly}
                      isInvalid={triedSave && !!issues.list}
                      errorMessage={issues.list ? t('projects:project:language_selector:' + issues.list) : undefined}
                      value={pair.list_id}
                      options={listOpts}
                      setValue={(val) => updatePair(i, { list_id: val ? val.toString() : '' })}>
              <>
              </>
            </Dropdown>
          </FlexRow>
        {!listFound &&
          <Warning>
            { t('projects:project:language_selector:list_not_found') }
          </Warning>
        }
        {/* Dragging is a mouse-only gesture, and this order decides what the
            guest is shown first — the same move stays reachable without one. */}
        {!readOnly &&
          <FlexRow align="center"
                   className="gap-[12px]">
            <IconButton icon="arrow_upward"
                        title={t('projects:project:language_selector:move_up')}
                        onClick={() => movePair(i, i - 1)} />
            <IconButton icon="arrow_downward"
                        title={t('projects:project:language_selector:move_down')}
                        onClick={() => movePair(i, i + 1)} />
            <Hint>
              { t('projects:project:language_selector:position_n', {n: i + 1, of: pairs.length}) }
            </Hint>
          </FlexRow>
        }
        </FlexRow>
      }
      </div>
      );
    })}

    {triedSave && validation.globalError &&
      <Warning>
        { t('projects:project:language_selector:' + validation.globalError) }
      </Warning>
    }

    {!readOnly && pairs.length < MAX_LANGUAGE_PAIRS &&
      <FlexRow justify="center"
               align="center"
               className="gap-[8px] w-full rounded-[8px] border-dashed border-1 border-[--Mid-Grey] py-[14px] cursor-pointer select-none hover:border-[--Primetime-main]"
               onClick={addPair}>
        <span className="material-icons !text-[--Primetime-main] text-[18px]">
          add
        </span>
        <span className="text-[14px] font-medium !text-[--Primetime-main]">
          { t('projects:project:language_selector:add') }
        </span>
      </FlexRow>
    }
    {pairs.length >= MAX_LANGUAGE_PAIRS &&
      <Hint>
        { t('projects:project:language_selector:max_reached') }
      </Hint>
    }
    </FlexRow>
  </FlexRow>
  );
};

export default LanguageSelectorSettings;
